<template>
  <div>
    <group-card :title="status_message" icon="rule">
      <template slot="actions">
        <div class="last-created-msg-container">
          <div class="last-created-msg" v-if="createdDateTime">This application was last assessed at {{createdDateTime.time}} on {{createdDateTime.date}} </div>
          <loading-icon-text-button
              v-if="refreshable"
              class="md-primary md-sm"
              @click="refreshAssessment"
              icon="refresh"
              tooltip="Run again"
              :loading="assessment && assessment.status == 'loading'">
                UPDATE ASSESSMENT NOW
          </loading-icon-text-button>
        </div>
      </template>

      <div class="outcome-table md-layout md-size-100">
          <template v-if="assessment && assessment.status == 'loading'" >
            <div class="md-layout md-alignment-center-center md-size-50">
              <md-card class="waiting-assessment-card">
                <md-card-header-text>
                  <div class="md-title waiting-assessment-title">Fetching Assessment...</div>
                </md-card-header-text>
                <md-card-content class="waiting-assessment-content">
                  <img :src="img_src" />
                  <p>Please wait whilst we are fetching the assessment.</p>
                </md-card-content>
              </md-card>
            </div>
          </template>
          <template v-for="(engine, index) in decision_engines" v-else>
            <div class="md-layout-item md-medium-size-33 md-small-size-50 md-size-20" v-if="show_last_card_from_adp(index)">
              <traffic-light-card
                :title="engine.engine_name"
                :status="engine_status(engine.status)"
                :same-line="false"
              >
                <div class="engine_items" v-for="{ code, description } in engine.reasons" :key="code">
                  <li :status="engine_status(engine.status)">{{ description }}</li>
                </div>
                <div class="engine_items" v-for="{ code, status, description } in engine.notes" :key="code">
                  <li :status="engine_status(status)">{{ description }}</li>
                </div>
                <div class="engine_items" v-if="engine.reasons.length==0">
                  <note-view :status="engine_status(engine.status)">{{ engine.status | capitalize}}</note-view>
                </div>
              </traffic-light-card>
            </div>
          </template>
          <template v-if="show_capacity_card">
            <div class="md-layout-item md-medium-size-33 md-small-size-50 md-size-20">
              <traffic-light-card
                :title="zinkCapacityCard.card.title"
                :status="zinkCapacityCard.card.status"
                :same-line="false"
              >
                <div class="engine_items">
                  <li :status="zinkCapacityCard.note.status">
                    {{ zinkCapacityCard.note.description }}
                  </li>
                </div>
              </traffic-light-card>
            </div>
          </template>
      </div>
    </group-card>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import filters from "@/components/utils/filters";
import ValidatedInputs from "@/components/ValidatedInput";
import GroupCard from "@/components/Cards/GroupCard";
import LoadingIconButton from "@/components/LoadingIconButton.vue";
import { NoteView } from "@/components";
import TrafficLightCard from "../Cards/TrafficLightCard";
import { hasVedaReport } from "@/components/utils/validation";
import moment from "moment";
import LoadingIconTextButton from "@/components/LoadingIconTextButton";

let img_src = require("@/assets/img/zink-logo-loading-animation-color.gif");

export default {
  components: {
    TrafficLightCard,
    LoadingIconButton,
    ...ValidatedInputs,
    GroupCard,
    NoteView,
    LoadingIconTextButton
  },
  props: {
    value: {
      type: Object,
    },
    refreshable: {
      type: Boolean,
      default: false,
    },
    zinkCapacityCard: {
      type: Object,
      default: () => {
        return {
          card: {
            title: 'Zink Capacity Rules',
            status: 'good'
          },
          note: {
            description: '',
            status: 'good'
          }
        }
      }
    }
  },
  data() {
    return {
      img_src
    };
  },
  computed: {
    ...mapGetters([
      "application",
      "is_ram_authorized"
    ]),
    // internal reference to the v-model on this component, handles incomeing and outgoing changes
    assessment: {
      get() {
        return this.value;
      },
      set(assessment) {
        this.$emit("input", assessment);
      },
    },

    createdDateTime() {
      if(this.value?.updated_at) {
        return {
          time : moment(this.value.updated_at).format("HH:mm"),
          date : moment(this.value.updated_at).format("DD-MM-YY")
        }
      }
      return null
    },
    status_message() {
      if (this.assessment == null || this.assessment.status == null) {
        return "Not Loaded";
      }
      if (this.assessment.status === 'loading') {
        return 'Loading...';
      }
      switch (this.zinkCapacityCard.card.status) {
        case "good":
          break;
        case "neutral":
          return "Warning";
        case "bad":
          return "Manual Assessment Required";
      }
      switch (this.assessment.status) {
        case "error":
          return "Error";
        case "decline":
          return "Manual Assessment Required";
        case "warning":
          return "Warning";
        case "accept":
          return "Accepted";
        default:
          return "";
      }
    },
    decision_engines() {
      return (this.assessment && this.assessment.decision_engines) || [];
    },
    show_capacity_card () {
      if (this.assessment == null || this.assessment.status == null) {
        return false;
      }
      if (this.zinkCapacityCard.card.status == 'good') {
        return false;
      }
      if (this.assessment && this.assessment.status == 'loading') {
        return false;
      }
      return true;
    },
  },
  mounted() {
    /* TODO: Reimplement, do not run this on mount will generate and upload docs to customer every since time assessment mounted
    if (!hasVedaReport(this.application.applicants[0].veda_report)) {
      try {
        let result = axios.post(`/api/v1/application/${this.application.loan.id}/generateDocuments`, this.application);
        if (Object.keys(result.data).length > 0) {
          this.showErrorDialog('Documents generated and mailed to customer', true);
        }
      } catch(error) {
        this.showErrorDialog('Failed to generate the documents', true);
      }
    }
    */
  },
  methods: {
    // make all filters available as methods
    ...filters,
    refreshAssessment() { // refresh button
      if (this.is_ram_authorized) {
        this.refreshRamAssessment();
      }
    },
    async refreshRamAssessment() {
      try {
        await this.$store.dispatch("fetch_ml_assessment",true);
        if (this.$modal.submit) {
          this.$modal.submit();
        }
      } catch(error) {
        console.log(error);
        this.$emit("setRamError", true);
      }
    },
    engine_status(status){
      switch (status) {
        case 'warning':
          return 'neutral';
        case 'accept':
          return 'good';
        case 'decline':
          return 'neutral';
        case 'error':
          return 'bad';
        case 'warning_greyed':
          return 'greyed';
        default:
          return 'information';
      }
    },
    show_last_card_from_adp (index) {
      if (index == 4) {
        if (this.assessment && this.assessment.status === 'accept' &&
            this.assessment.decision_engines[index].status === 'accept' &&
            this.zinkCapacityCard.card.status !== 'good') {
          return false;
        }
      }
      return true;
    },
  },
  filters: {
    // make all filters available as filters
    ...filters,
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/theme.scss";

.good {
  color: $color-good !important;
}
.neutral {
  color: $color-neutral !important;
}
.bad {
  color: $color-bad !important;
}
.information {
  color: $color-information !important;
}

.greyed {
  color: $color-grey !important;
}

.outcome-table {
  & td {
    padding-top: 10px;
    font-weight: bold;
  }

  & td .note {
    font-weight: normal;
  }

  & td:first-child {
    width: 50px;
  }
}

.waiting-assessment-card{
  background: $ausloans-blue-hover !important;
  color: $color-text-on-dark !important;
  width: auto;

  .waiting-assessment-title {
    padding-top: 20px;
  }

  .waiting-assessment-content {
    display: flex;
    flex-direction: column;

    & img {
      width: 100px;
      align-self: center;
    }

    & p {
      margin-top: 16px;
    }
  }
}
.last-created-msg-container {
  width: 100%;
  display: flex;
  justify-content: center;
}
.last-created-msg {
  margin-right: 20px;
  margin-top: 6px;
  font-size: 1.2rem;
}
</style>
